import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * Create context helper that provides a context accessor function
 *
 * @param {string} rootComponentName component name used in errors if context used outside of provider
 * @param {React.ContextType} defaultContext context used as a fallback
 * @returns {[function,function]} Provider and context accessor functions
 */
export default function createContext(rootComponentName, defaultContext) {
  const Ctx = React.createContext(defaultContext);

  function Provider(props) {
    const { children, ...context } = props;
    const value = useMemo(
      () => context,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      Object.values(context)
    );
    return <Ctx.Provider value={ value }>{children}</Ctx.Provider>;
  }

  Provider.propTypes = {
    children: PropTypes.node.isRequired
  };

  function useContext(callerComponentName) {
    const context = React.useContext(Ctx);
    if (context) {
      return context;
    }
    if (defaultContext) {
      return defaultContext;
    }
    throw Error(
      `${callerComponentName} must be rendered inside of a ${rootComponentName} component.`
    );
  }

  Ctx.displayName = `${rootComponentName}Context`;
  Provider.displayName = `${rootComponentName}Provider`;
  return [Provider, useContext];
}
